import { useState } from 'react'
import { Submissions } from '../api'
import type { CorrectionSchema, CurrentReading } from '../api'
import { errorSentence } from '../lib/errorSentence'
import { COMMON_SPECIES_NAMES } from '../speciesCommonNames'

const MM_PER_INCH = 25.4
const G_PER_POUND = 453.592

const fmt = (x: number) => (Math.round(x * 10) / 10).toString()
function mmToIn(mm: number | null | undefined): string {
  return mm == null ? '' : fmt(mm / MM_PER_INCH)
}
function gToLb(g: number | null | undefined): string {
  return g == null ? '' : fmt(g / G_PER_POUND)
}

interface Props {
  submissionId: string
  /** The current reading; inputs start from its values, placeholders show our estimate. */
  reading: CurrentReading
  onSaved: (correction: CorrectionSchema) => void
}

/** Edits the current reading. A blank input sends nothing (no opinion); "use
 *  estimate" fills in our number so the saved row matches what we said and
 *  overrides an earlier correction of that field. */
export default function CorrectionForm({ submissionId, reading, onSaved }: Props) {
  // Only fields someone has already corrected start filled in; an untouched
  // field stays blank (= no opinion) so saving doesn't re-source our own
  // estimate as "entered". The placeholder shows the estimate.
  const own = <T,>(f: { source: string }, v: T, blank: T) => (f.source === 'estimate' ? blank : v)
  const [lengthIn, setLengthIn] = useState(() => own(reading.length, mmToIn(reading.length.value), ''))
  const [girthIn, setGirthIn] = useState(() => own(reading.girth, mmToIn(reading.girth.value), ''))
  const [weightLb, setWeightLb] = useState(() => own(reading.weight, gToLb(reading.weight.value), ''))
  const [species, setSpecies] = useState(() => own(reading.species, reading.species.common ?? reading.species.scientific ?? '', ''))
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const r = await Submissions.upsertCorrection({
        correctionUpsertRequest: {
          submissionId,
          lengthMm: lengthIn ? Number(lengthIn) * MM_PER_INCH : undefined,
          girthMm: girthIn ? Number(girthIn) * MM_PER_INCH : undefined,
          weightG: weightLb ? Number(weightLb) * G_PER_POUND : undefined,
          species: species || undefined,
          note: note || undefined,
        },
      })
      onSaved(r.correction)
    } catch (e) {
      setError(await errorSentence(e, 'Could not save the correction — try again.'))
    } finally {
      setBusy(false)
    }
  }

  const numeric = (
    label: string,
    value: string,
    set: (v: string) => void,
    estimate: string,
  ) => (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] tracking-[0.1em] uppercase text-stone-500">{label}</span>
      <input type="number" step="0.1" inputMode="decimal" value={value} onChange={e => set(e.target.value)}
        placeholder={estimate ? `est. ${estimate}` : ''}
        className="border border-stone-300 rounded px-2 py-1.5 text-base" />
      {estimate && value !== estimate && (
        <button type="button" onClick={() => set(estimate)}
          className="self-start font-mono text-[10px] text-stone-400 hover:text-stone-700 underline">
          use estimate
        </button>
      )}
    </label>
  )

  return (
    <form onSubmit={save} className="space-y-2 text-sm">
      <div className="grid grid-cols-3 gap-2">
        {numeric('Length (in)', lengthIn, setLengthIn, mmToIn(reading.length.estimate))}
        {numeric('Girth (in)', girthIn, setGirthIn, mmToIn(reading.girth.estimate))}
        {numeric('Weight (lb)', weightLb, setWeightLb, gToLb(reading.weight.estimate))}
      </div>
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[10px] tracking-[0.1em] uppercase text-stone-500">Species</span>
        <input list="correction-species-options" value={species} onChange={e => setSpecies(e.target.value)}
          placeholder={reading.species.estimateCommon ? `est. ${reading.species.estimateCommon}` : ''}
          className="border border-stone-300 rounded px-2 py-1.5 text-base" />
        <datalist id="correction-species-options">
          {COMMON_SPECIES_NAMES.map(name => <option key={name} value={name} />)}
        </datalist>
      </label>
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[10px] tracking-[0.1em] uppercase text-stone-500">Note</span>
        <input value={note} onChange={e => setNote(e.target.value)}
          className="border border-stone-300 rounded px-2 py-1.5 text-base" />
      </label>
      {error && <p className="font-mono text-[11px] text-coral">{error}</p>}
      <button type="submit" disabled={busy}
        className="w-full sm:w-auto font-mono text-[11px] tracking-[0.15em] uppercase bg-bear text-white px-4 py-2.5 rounded-lg hover:bg-bear-light disabled:opacity-50">
        {busy ? 'Saving…' : 'Save reading'}
      </button>
    </form>
  )
}
